import React from 'react';
import { Button } from '../../components/ui/button';
import { COUNTRIES, getStatesForCountry } from '../../lib/geoData';
import { detectStateFromPostal } from '../../lib/postalCodeAutoDetect';

/**
 * Controlled shipping-address form for the Customer Info step. All state
 * lives in the parent (`CustomerInfoPage`); this component is purely
 * presentational + emits events for changes and the "add item" action.
 *
 * Props:
 *   formData     — current form state object
 *   onChange     — (event) => void, wires to each input's onChange
 *   onFileChange — (event) => void, for the optional PO PDF upload
 *   poFile       — currently-selected PO file (for the confirmation label)
 *   onAddItem    — called when user clicks "ADD NEW ITEM TO MY ORDER"
 *
 * iter110: Country is now picked from the full ISO list (US + Canada
 * pinned at top, then alphabetical). State/Province becomes a dropdown
 * for Canada (13 provinces+territories) and the US (65 states+territories),
 * and a plain text input for everywhere else — matches Register exactly
 * so the tax calculator's province lookup gets canonical strings.
 *
 * iter114s.59 — Postal-code auto-detect: when the customer types a
 * Canadian postal code or US ZIP, we infer the province/state and
 * pre-select the dropdown so they don't have to scroll-and-pick on
 * top of typing the code (operator brief: "Auto-detect and populate
 * State/Province based on entered address data… Reduce manual entry
 * requirements wherever possible"). Live as they type — fires on
 * every change, not just blur, so the dropdown reflects the in-flight
 * intent immediately.
 */
export const ShippingAddressForm = ({
  formData,
  onChange,
  onFileChange,
  poFile,
  onAddItem,
}) => {
  const stateOptions = getStatesForCountry(formData.country);

  // Intercept postal-code edits to also auto-fill State/Province.
  // Emits the original change event verbatim so the parent's setState
  // still runs untouched, then — if a province/state was successfully
  // inferred AND the customer hasn't already typed one — emits a
  // second synthetic event setting `state_province`. We never
  // overwrite a value the customer has explicitly chosen.
  const handlePostalChange = (e) => {
    onChange(e);
    const inferred = detectStateFromPostal(e.target.value, formData.country);
    if (inferred && inferred !== formData.state_province) {
      // Avoid clobbering a user-typed state — only auto-fill when
      // the field is empty OR the dropdown's current selection was
      // produced by a previous auto-detect for the same postal code.
      if (!formData.state_province || formData.state_province === '') {
        onChange({ target: { name: 'state_province', value: inferred } });
      }
    }
  };
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-2">Shipping Address Details</h2>
      <p className="text-sm text-gray-600 mb-6">
        All fields marked with <span className="text-red-500">*</span> are required.
        Your order will be shipped to the address you provide below.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Customer PO Number (Optional)
          </label>
          <input
            type="text"
            name="customer_po_number"
            value={formData.customer_po_number}
            onChange={onChange}
            placeholder="Your purchase order number"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
            data-testid="customer-po-number-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Upload Purchase Order (Optional)
          </label>
          <input
            type="file"
            accept=".pdf"
            onChange={onFileChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#4A7C59] file:text-white hover:file:bg-[#3d6849]"
            data-testid="customer-po-file-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            PDF only, max 10MB
            {poFile && <span className="text-green-600 font-medium"> • {poFile.name} selected</span>}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={onChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
              data-testid="customer-first-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={onChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
              data-testid="customer-last-name-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
            data-testid="customer-company-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Shipping Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={onChange}
            required
            placeholder="Street address, P.O. box, company name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
            data-testid="customer-address-line1-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2 (Optional)
          </label>
          <input
            type="text"
            name="address_line2"
            value={formData.address_line2}
            onChange={onChange}
            placeholder="Apartment, suite, unit, building, floor, etc."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
            data-testid="customer-address-line2-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Telephone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
            data-testid="customer-phone-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={onChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
            data-testid="customer-email-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Town / City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={onChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
              data-testid="customer-city-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              name="country"
              value={formData.country}
              onChange={onChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
              data-testid="customer-country-select"
            >
              <option value="">Select a country</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Province / State <span className="text-red-500">*</span>
            </label>
            {stateOptions ? (
              <select
                name="state_province"
                value={formData.state_province}
                onChange={onChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
                data-testid="customer-state-select"
              >
                <option value="">Select a province / state</option>
                {stateOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="state_province"
                value={formData.state_province}
                onChange={onChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
                data-testid="customer-state-input"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postal / ZIP Code <span className="text-red-500">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-wide text-[#4A7C59] font-normal">
                Auto-fills province/state
              </span>
            </label>
            <input
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={handlePostalChange}
              required
              placeholder={formData.country === 'Canada' ? 'A1A 1A1' : '12345'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
              data-testid="customer-postal-code-input"
            />
          </div>
        </div>
      </div>

      <Button
        type="button"
        onClick={onAddItem}
        className="w-full mt-6 bg-[#4A7C59] hover:bg-[#3d6849] text-white"
        data-testid="customer-add-item-btn"
      >
        + ADD NEW ITEM TO MY ORDER
      </Button>
    </div>
  );
};
